<script setup lang="ts">
import { fileOpen } from '~/lib/file-open'
import type { mastodon } from '#shared/types'

interface ImageState {
  x: number
  y: number
  resource: HTMLImageElement | null
  width?: number
  height?: number
}

interface State {
  drag: boolean
  my: number | null
  mx: number | null
  image: ImageState
}

const props = withDefaults(defineProps<{
  account: mastodon.v1.Account
  image?: string
  accept?: string
  placeholderSvg?: string
  borderRadius?: number
  width?: number
  border?: number
  height?: number
  color?: number[]
}>(), {
  image: '',
  width: 800,
  height: 800,
  border: 0,
  borderRadius: 0,
  color: () => [0, 0, 0, 0.5],
  placeholderSvg: '',
  accept: 'image/jpeg, image/png, image/gif, image/webp, image/avif, video/webm',
})

const canvas = ref<HTMLCanvasElement | null>(null)
const context = ref<CanvasRenderingContext2D | null>(null)
const scale = ref(1)
const rotation = ref(0)
const imageLoaded = ref(false)

const elementScale = ref(0)

const state = reactive<State>({
  drag: false,
  my: null,
  mx: null,
  image: {
    x: 0,
    y: 0,
    resource: null,
  },
})

const cursorClass = ref('cursorPointer')
let dragged = false
let changed = false

const dimensions = computed(() => {
  return {
    width: props.width,
    height: props.height,
    border: props.border,
    canvas: {
      width: props.width + (props.border * 2),
      height: props.height + (props.border * 2),
    },
  }
})

const canvasWidth = computed(() => props.width + (props.border * 2))
const canvasHeight = computed(() => props.height + (props.border * 2))
const rotationRadian = computed(() => rotation.value * Math.PI / 180)

onMounted(() => {
  if (canvas.value) {
    context.value = canvas.value.getContext('2d') as CanvasRenderingContext2D
    paint(context.value, [255, 0, 0, 1], props.border)
  }

  if (!props.image && props.placeholderSvg) {
    const placeHolder = svgToImage(props.placeholderSvg)

    placeHolder.onload = function () {
      const x = canvasWidth.value / 2 - props.width / 2
      const y = canvasHeight.value / 2 - props.height / 2
      context.value?.drawImage(placeHolder, x, y, props.width, props.height)
    }
  }
  else {
    loadImage(props.image ?? '')
  }
  window.addEventListener('resize', () => {
    elementScale.value = getElementScale()
  })
})

watch(
  () => state,
  () => {
    if (imageLoaded.value) {
      redraw()
    }
  },
  { deep: true },
)
watch(scale, () => {
  if (imageLoaded.value) {
    replaceImageInBounds()
    redraw()
  }
})
watch(rotation, () => {
  if (imageLoaded.value) {
    replaceImageInBounds()
    redraw()
  }
})
watch(
  () => props.borderRadius,
  () => { redraw() },
)

function getElementScale() {
  const { width = canvasWidth.value } = canvas.value?.getBoundingClientRect?.() ?? {}
  return width / canvasWidth.value
}

function drawRoundedRect(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, borderRadius: number) {
  if (borderRadius === 0) {
    context.rect(x, y, width, height)
  }
  else {
    const widthMinusRad = width - borderRadius
    const heightMinusRad = height - borderRadius
    context.translate(x, y)
    context.arc(borderRadius, borderRadius, borderRadius, Math.PI, Math.PI * 1.5)
    context.lineTo(widthMinusRad, 0)
    context.arc(widthMinusRad, borderRadius, borderRadius, Math.PI * 1.5, Math.PI * 2)
    context.lineTo(width, heightMinusRad)
    context.arc(widthMinusRad, heightMinusRad, borderRadius, Math.PI * 2, Math.PI * 0.5)
    context.lineTo(borderRadius, height)
    context.arc(borderRadius, heightMinusRad, borderRadius, Math.PI * 0.5, Math.PI)
    context.translate(-x, -y)
  }
}

function svgToImage(rawSVG: string) {
  const svg = new Blob([rawSVG], { type: 'image/svg+xmlcharset=utf-8' })
  const domURL = self.URL || self.webkitURL || self
  const url = domURL.createObjectURL(svg)
  const img = new Image()
  img.src = url
  return img
}

function paint(context: CanvasRenderingContext2D, color: number[], borderRadius: number) {
  context.save()
  context.translate(0, 0)
  context.fillStyle = 'rgba(' + color.slice(0, 4).join(',') + ')'
  const borderSize = props.border
  const height = canvasHeight.value
  const width = canvasWidth.value

  // clamp border radius between zero (perfect rectangle) and half the size without borders (perfect circle or "pill")
  borderRadius = Math.max(borderRadius, 0)
  borderRadius = Math.min(borderRadius, width / 2 - borderSize, height / 2 - borderSize)

  context.beginPath()

  // inner rect, possibly rounded
  drawRoundedRect(
    context,
    borderSize,
    borderSize,
    width - borderSize * 2,
    height - borderSize * 2,
    borderRadius)

  context.rect(width, 0, -width, height) // outer rect, drawn "counterclockwise"
  context.fill('evenodd')
  context.restore()
}

function onDrop(e: DragEvent) {
  e.stopPropagation()
  e.preventDefault()

  if (e.dataTransfer && e.dataTransfer.files.length) {
    const reader = new FileReader()
    const file = e.dataTransfer.files[0]
    changed = true
    reader.onload = e => loadImage(`${e.target?.result}`)
    reader.readAsDataURL(file)
  }
}

function onDragStart(e: MouseEvent | TouchEvent) {
  if (e.type !== 'touchstart') {
    e.preventDefault()
  }
  state.drag = true
  state.mx = null
  state.my = null
  cursorClass.value = 'cursorGrabbing'
  const eventSubject = document
  let hasMoved = false

  function handleMouseUp(event: MouseEvent | TouchEvent) {
    onDragEnd()
    if (!hasMoved && (event as TouchEvent).targetTouches) {
      (event.target as HTMLElement)?.click()
    }
    eventSubject.removeEventListener('mouseup', handleMouseUp)
    eventSubject.removeEventListener('mousemove', handleMouseMove)
    eventSubject.removeEventListener('touchend', handleMouseUp)
    eventSubject.removeEventListener('touchmove', handleMouseMove)
  }

  function handleMouseMove(event: MouseEvent | TouchEvent) {
    hasMoved = true
    onMouseMove(event)
  }

  eventSubject.addEventListener('mouseup', handleMouseUp)
  eventSubject.addEventListener('mousemove', handleMouseMove)
  eventSubject.addEventListener('touchend', handleMouseUp)
  eventSubject.addEventListener('touchmove', handleMouseMove)
}

function onDragEnd() {
  if (state.drag) {
    state.drag = false
    cursorClass.value = 'cursorPointer'
  }
}

function onMouseMove(e: MouseEvent | TouchEvent) {
  if (state.drag === false) {
    return
  }
  dragged = true
  changed = true

  const imageState = state.image
  const lastX = imageState.x
  const lastY = imageState.y

  const mousePositionX = (e as TouchEvent).targetTouches ? (e as TouchEvent).targetTouches[0].pageX : (e as MouseEvent).clientX
  const mousePositionY = (e as TouchEvent).targetTouches ? (e as TouchEvent).targetTouches[0].pageY : (e as MouseEvent).clientY

  const newState = {
    mx: mousePositionX,
    my: mousePositionY,
    image: imageState,
  }

  if (state.mx && state.my) {
    if (!elementScale.value) {
      elementScale.value = getElementScale()
    }
    const xDiff = (state.mx - mousePositionX) / scale.value / elementScale.value
    const yDiff = (state.my - mousePositionY) / scale.value / elementScale.value
    imageState.y = getBoundedY(lastY - yDiff, scale.value)
    imageState.x = getBoundedX(lastX - xDiff, scale.value)
  }

  state.mx = newState.mx
  state.my = newState.my
  state.image = imageState
}

function replaceImageInBounds() {
  const imageState = state.image
  imageState.y = getBoundedY(imageState.y, scale.value)
  imageState.x = getBoundedX(imageState.x, scale.value)
}

function loadImage(imageURL: string) {
  const img = new Image()
  img.onload = () => {
    const imageState = getInitialSize(img.width, img.height)
    state.image.x = 0
    state.image.y = 0
    state.image.resource = img
    state.image.width = imageState.width
    state.image.height = imageState.height
    state.drag = false
    imageLoaded.value = true
    cursorClass.value = 'cursorGrab'
  }
  img.onerror = err => console.error('error loading image: ', err)
  if (!isDataURL(imageURL)) {
    img.crossOrigin = 'anonymous'
  }
  img.src = imageURL
}

function getInitialSize(width: number, height: number) {
  let newHeight
  let newWidth

  const dim = dimensions.value
  const canvasRatio = dim.height / dim.width
  const imageRatio = height / width

  if (canvasRatio > imageRatio) {
    newHeight = (dim.height)
    newWidth = (width * (newHeight / height))
  }
  else {
    newWidth = (dim.width)
    newHeight = (height * (newWidth / width))
  }

  return {
    height: newHeight,
    width: newWidth,
  }
}

function isDataURL(str: string) {
  if (str === null) {
    return false
  }
  return !!str.match(/^\s*data:([a-z]+\/[a-z]+([a-z\-]+=[a-z\-]+)?)?(base64)?,[a-z0-9!$&',()*+=\-._~:@\/?%\s]*\s*$/i) // eslint-disable-line no-useless-escape
}

function getBoundedX(x: number, scale: number) {
  const image = state.image
  const dim = dimensions.value
  const width = Math.abs((image.width ?? 0) * Math.cos(rotationRadian.value)) + Math.abs((image.height ?? 0) * Math.sin(rotationRadian.value))
  let widthDiff = Math.floor((width - dim.width / scale) / 2)
  widthDiff = Math.max(0, widthDiff)
  return Math.max(-widthDiff, Math.min(x, widthDiff))
}

function getBoundedY(y: number, scale: number) {
  const image = state.image
  const dim = dimensions.value
  const height = Math.abs((image.width ?? 0) * Math.sin(rotationRadian.value)) + Math.abs((image.height ?? 0) * Math.cos(rotationRadian.value))
  let heightDiff = Math.floor((height - dim.height / scale) / 2)
  heightDiff = Math.max(0, heightDiff)
  return Math.max(-heightDiff, Math.min(y, heightDiff))
}

function paintImage(context: CanvasRenderingContext2D, image: ImageState, border: number) {
  if (image.resource) {
    const position = calculatePosition(image, border)
    context.save()
    context.globalCompositeOperation = 'destination-over'
    context.translate(canvasWidth.value / 2, canvasHeight.value / 2)
    context.rotate(rotationRadian.value)
    context.translate(-canvasWidth.value / 2, -canvasHeight.value / 2)
    context.drawImage(
      image.resource,
      position.x,
      position.y,
      position.width,
      position.height)
    context.restore()
  }
}

function transformDataWithRotation(x: number, y: number) {
  const radian = -rotationRadian.value
  const rx = x * Math.cos(radian) - y * Math.sin(radian)
  const ry = x * Math.sin(radian) + y * Math.cos(radian)
  return [rx, ry]
}

function calculatePosition(image: ImageState = state.image, border: number) {
  image = image || state.image
  const dim = dimensions.value
  const width = (image.width ?? 0) * scale.value
  const height = (image.height ?? 0) * scale.value
  const widthDiff = (width - dim.width) / 2
  const heightDiff = (height - dim.height) / 2
  const x0 = image.x * scale.value
  const y0 = image.y * scale.value
  let [x, y] = transformDataWithRotation(x0, y0)
  x += border - widthDiff
  y += border - heightDiff
  return {
    x,
    y,
    height,
    width,
  }
}

function redraw() {
  if (!context.value) {
    return
  }
  context.value?.clearRect(0, 0, canvasWidth.value, canvasHeight.value)
  paint(context.value, props.color, props.borderRadius)
  paintImage(context.value, state.image, props.border)
}

function getImage() {
  const cropRect = getCroppingRect()
  const image = state.image
  if (!image.resource) {
    return null
  }
  // get actual pixel coordinates
  cropRect.x *= image.resource.width
  cropRect.y *= image.resource.height
  cropRect.width *= image.resource.width
  cropRect.height *= image.resource.height
  // create a canvas with the correct dimensions
  const canvas = document.createElement('canvas')
  canvas.width = cropRect.width
  canvas.height = cropRect.height
  // draw the full-size image at the correct position,
  // the image gets truncated to the size of the canvas.
  canvas.getContext('2d')?.drawImage(image.resource, -cropRect.x, -cropRect.y)
  return canvas
}

function getImageScaled() {
  const canvas = document.createElement('canvas')
  canvas.width = props.width
  canvas.height = props.height
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return null
  }
  // don't paint a border here, as it is the resulting image
  paintImage(ctx, state.image, 0)
  return canvas
}

function imageChanged() {
  return changed
}

function getCroppingRect() {
  const frameRect = {
    x: props.border,
    y: props.border,
    width: props.width,
    height: props.height,
  }
  const imageRect = calculatePosition(state.image, props.border)
  return {
    x: (frameRect.x - imageRect.x) / imageRect.width,
    y: (frameRect.y - imageRect.y) / imageRect.height,
    width: frameRect.width / imageRect.width,
    height: frameRect.height / imageRect.height,
  }
}

async function onClick() {
  if (dragged === true) {
    dragged = false
  }
  else {
    const files = await fileOpen({ multiple: false, mimeTypes: props.accept.split(', ') })
    if (files.length) {
      loadFile(files[0])
    }
  }
}

function loadFile(file: File) {
  const reader = new FileReader()
  reader.onload = () => loadImage(`${reader.result}`)
  changed = true
  reader.readAsDataURL(file)
}

function resetImage() {
  if (!context.value) {
    return
  }
  imageLoaded.value = false
  state.drag = false
  state.my = null
  state.mx = null
  state.image = {
    x: 0,
    y: 0,
    resource: null,
  }
  context.value?.clearRect(0, 0, canvasWidth.value, canvasHeight.value)
  paint(context.value, props.color, props.borderRadius)

  const placeHolder = svgToImage(props.placeholderSvg)

  placeHolder.onload = function () {
    const x = canvasWidth.value / 2 - props.width / 2
    const y = canvasHeight.value / 2 - props.height / 2
    context.value?.drawImage(placeHolder, x, y, props.width, props.height)
  }
}

const processing = ref(false)
const emit = defineEmits(['saved'])
function save() {
  processing.value = true
  getImageScaled()?.toBlob(async (blob) => {
    if (blob) {
      const formData = new FormData()
      const avatar = new File([blob], `${crypto.randomUUID()}.jpg`, { lastModified: new Date().getTime(), type: blob.type })
      formData.append('avatar', avatar)
      const response = await $fetch<mastodon.v1.Account>('/api/accounts/update_credentials', {
        headers: useRequestHeaders(['cookie']),
        method: 'PATCH',
        body: formData,
      })
      emit('saved', response)
      processing.value = false
    }
  }, 'image/jpeg', 1)
}

defineExpose({
  getImage,
  getImageScaled,
  imageChanged,
  resetImage,
})
</script>

<template>
  <div class="account-avatar-editor">
    <canvas
      id="avatarEditorCanvas"
      ref="canvas"
      :width="canvasWidth"
      :height="canvasHeight"
      :class="cursorClass"
      @dragover.prevent
      @drop="onDrop"
      @mousedown="onDragStart"
      @touchstart="onDragStart"
      @click="onClick"
    />
    <template v-if="!processing">
      <InputRange v-model="scale" min="1" max="3" step="0.02" title="Scale" />
      <button @click="save">
        {{ $t('action-save') }}
      </button>
    </template>
    <UiProcessingOverlay v-else />
  </div>
</template>

<style lang="scss">
.account-avatar-editor {
  aspect-ratio: 1 / 1;
  width: 100%;
  position: relative;

  canvas {
    display: block;
    width: 100%;
  }

  .input-range {
    --thumb-color: rgba(255, 255, 255, 0.496);
    --thumb-border-w: 0;
    position: absolute;
    bottom: 15%;
    left: 50%;
    transform: translateX(-50%);
    width: 55%;

    .bg {
      background: rgb(132 132 132 / 0.4);
      backdrop-filter: blur(5px);
    }
  }

  button {
    position: absolute;
    bottom: 5%;
    left: 50%;
    transform: translateX(-50%);
    background: rgb(132 132 132 / 0.4);
    backdrop-filter: blur(5px);
  }
}

.cursorPointer {
  cursor: pointer
}

.cursorGrab {
  cursor: grab;
  cursor: -webkit-grab;
  cursor: -moz-grab;
}

.cursorGrabbing {
  cursor: grabbing;
  cursor: -webkit-grabbing;
  cursor: -moz-grabbing;
}
</style>
